import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useAppState } from '../hooks/useAppState';
import { useProject } from './ProjectContext';
import { ConfirmDialog, AlertDialog } from '../components/CustomDialog';

type AppStateReturnType = ReturnType<typeof useAppState>;

interface UIContextType extends AppStateReturnType {
  customConfirm: (
    title: string,
    message: string,
    type?: 'danger' | 'info' | 'warning',
    confirmText?: string,
    cancelText?: string
  ) => Promise<boolean>;
  customAlert: (
    title: string,
    message: string,
    type?: 'success' | 'error' | 'info',
    confirmText?: string
  ) => Promise<void>;
}

export const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tasks, allTasksGlobal, currentProjectId, allProjects, globalMetadata, activeWorkspace } = useProject();
  const state = useAppState(tasks, allTasksGlobal, currentProjectId, allProjects, globalMetadata, activeWorkspace);

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    type: 'danger' | 'info' | 'warning';
    resolve?: (val: boolean) => void;
  } | null>(null);

  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    type: 'success' | 'error' | 'info';
    resolve?: () => void;
  } | null>(null);

  const customConfirm = useCallback((
    title: string,
    message: string,
    type: 'danger' | 'info' | 'warning' = 'info',
    confirmText?: string,
    cancelText?: string
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        resolve
      });
    });
  }, []);

  const customAlert = useCallback((
    title: string,
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    confirmText?: string
  ): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({
        isOpen: true,
        title,
        message,
        confirmText,
        type,
        resolve
      });
    });
  }, []);

  const value = useMemo(() => ({
    ...state,
    customConfirm,
    customAlert
  }), [state, customConfirm, customAlert]) as UIContextType;

  return (
    <UIContext.Provider value={value}>
      {children}
      {confirmState && (
        <ConfirmDialog
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          type={confirmState.type}
          onConfirm={() => {
            if (confirmState.resolve) confirmState.resolve(true);
            setConfirmState(null);
          }}
          onClose={() => {
            if (confirmState.resolve) confirmState.resolve(false);
            setConfirmState(null);
          }}
        />
      )}
      {alertState && (
        <AlertDialog
          isOpen={alertState.isOpen}
          title={alertState.title}
          message={alertState.message}
          confirmText={alertState.confirmText}
          type={alertState.type}
          onClose={() => {
            if (alertState.resolve) alertState.resolve();
            setAlertState(null);
          }}
        />
      )}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export const useUIOptional = () => useContext(UIContext);
